import {
  addDays,
  addMonths,
  addWeeks,
  endOfMonth,
  endOfWeek,
  format,
  isValid,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
  subWeeks,
} from 'date-fns'
import { useMemo } from 'react'
import type { Filter, FilterConfigMap } from '#src/components/filter/Filter.tsx'
import { FILTER_OPERATORS } from '#src/components/filter/operators.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'

export function useFilteredData<T>(
  data: T[] | undefined,
  filterOptions: FilterConfigMap<T>,
  filters: Filter[]
): T[] {
  const currentPractitioner = useCurrentPractitioner()
  const mappedFilters = useMemo(
    () =>
      filters.map((f) => ({
        ...f,
        value: f.value.map((v) =>
          v === FilterValue.CurrentUser
            ? currentPractitioner.reference.reference
            : v
        ),
      })),
    [filters, currentPractitioner.reference.reference]
  )
  return useMemo(() => {
    if (!data) {
      return []
    }

    if (mappedFilters.length === 0) {
      return data
    }

    return data.filter((item) =>
      mappedFilters.every((filter) => {
        if (!filter.value || filter.value.length === 0) {
          return true
        }

        const filterConfig = filterOptions[filter.field]
        if (!filterConfig) {
          return true
        }

        const operator =
          filter.operator ??
          filterConfig.defaultOperator ??
          FILTER_OPERATORS[filterConfig.type][0].value

        if (filterConfig.type === 'multi-select') {
          let itemValue = filterConfig.valueFn(item)
          // Treat empty string as 'none' for matching, but only for multi-select fields.
          // This is because multi-select filters often represent 'no value' (e.g., no owner) as '' or 'none',
          // whereas other filter types (text, date, boolean) do not have this semantic and should not coerce ''.
          if (itemValue === '') {
            itemValue = FilterValue.None
          }
          const isMatch = filter.value.includes(itemValue)
          return operator === 'not-in' ? !isMatch : isMatch
        }

        if (filterConfig.type === 'multi-select-array') {
          const itemValue = filterConfig.valueFn(item)
          const isEmpty = itemValue.length === 0
          // FilterValue.None sentinel matches items with an empty array value
          // (e.g. docs that bypassed the AI classifier and have no categories).
          const filterValuesExcludingNone = filter.value.filter(
            (v) => v !== FilterValue.None
          )
          const filterIncludesNone = filter.value.includes(FilterValue.None)
          if (operator === 'in' || operator === 'not-in') {
            const isMatch =
              (filterIncludesNone && isEmpty) ||
              (filterValuesExcludingNone.length > 0 &&
                filterValuesExcludingNone.every((v) => itemValue.includes(v)))
            return operator === 'not-in' ? !isMatch : isMatch
          }
          if (operator === 'contains' || operator === 'not-contains') {
            const isMatch =
              (filterIncludesNone && isEmpty) ||
              filterValuesExcludingNone.some((v) => itemValue.includes(v))
            return operator === 'contains' ? isMatch : !isMatch
          }
          throw new Error(`Unsupported operator: ${operator}`)
        }

        if (filterConfig.type === 'text') {
          // This path previously used Fuse.js to search but was removed due to
          // performance issues across thousands of data points causing main thread blocking.
          // A few options for re-introducing Fuse:
          // 1. Set a threshold for when to use Fuse.js vs. `includes` (i.e. 1000 data points). Sub-optimal due to non-deterministic behavior.
          // 2. Defer text-based searches to the end of the filter pipeline
          //    to ensure fewer data points before running Fuse. This doesn't work if only searching on text so may need to be layered with a threshold.
          // 3. Push Fuse search to web worker to avoid blocking the main thread. Still a slow operation and need to consider UX.
          const isMatch = filterConfig
            .valueFn(item)
            .toLowerCase()
            .includes(filter.value[0].toLowerCase())
          return operator === 'not-contains' ? !isMatch : isMatch
        }

        if (filterConfig.type === 'date') {
          const itemValue = filterConfig.valueFn(item)
          if (
            operator === 'not-equals' &&
            filter.value[0] === FilterValue.None
          ) {
            // Match if itemValue is NOT empty/null/undefined
            return !!itemValue && itemValue !== ''
          }
          if (filter.value[0] === FilterValue.None) {
            // Match if itemValue is empty, null, or undefined
            return !itemValue || itemValue === ''
          }
          const itemDate = parseISO(itemValue)
          if (!isValid(itemDate)) {
            return false
          }
          const itemLocalDateStr = format(itemDate, 'yyyy-MM-dd')
          const today = startOfDay(new Date())
          const todayStr = format(today, 'yyyy-MM-dd')

          // Handle relative date operators
          if (operator === 'is_today') {
            return itemLocalDateStr === todayStr
          }
          if (operator === 'is_yesterday') {
            const yesterday = format(subDays(today, 1), 'yyyy-MM-dd')
            return itemLocalDateStr === yesterday
          }
          if (operator === 'is_tomorrow') {
            const tomorrow = format(addDays(today, 1), 'yyyy-MM-dd')
            return itemLocalDateStr === tomorrow
          }
          if (operator === 'is_past') {
            return itemLocalDateStr < todayStr
          }
          if (operator === 'is_future') {
            return itemLocalDateStr > todayStr
          }
          if (operator === 'is_this_week') {
            const weekStart = format(startOfWeek(today), 'yyyy-MM-dd')
            const weekEnd = format(endOfWeek(today), 'yyyy-MM-dd')
            return itemLocalDateStr >= weekStart && itemLocalDateStr <= weekEnd
          }
          if (operator === 'is_last_week') {
            const lastWeek = subWeeks(today, 1)
            const weekStart = format(startOfWeek(lastWeek), 'yyyy-MM-dd')
            const weekEnd = format(endOfWeek(lastWeek), 'yyyy-MM-dd')
            return itemLocalDateStr >= weekStart && itemLocalDateStr <= weekEnd
          }
          if (operator === 'is_next_week') {
            const nextWeek = addWeeks(today, 1)
            const weekStart = format(startOfWeek(nextWeek), 'yyyy-MM-dd')
            const weekEnd = format(endOfWeek(nextWeek), 'yyyy-MM-dd')
            return itemLocalDateStr >= weekStart && itemLocalDateStr <= weekEnd
          }
          if (operator === 'is_this_month') {
            const monthStart = format(startOfMonth(today), 'yyyy-MM-dd')
            const monthEnd = format(endOfMonth(today), 'yyyy-MM-dd')
            return (
              itemLocalDateStr >= monthStart && itemLocalDateStr <= monthEnd
            )
          }
          if (operator === 'is_last_month') {
            const lastMonth = subMonths(today, 1)
            const monthStart = format(startOfMonth(lastMonth), 'yyyy-MM-dd')
            const monthEnd = format(endOfMonth(lastMonth), 'yyyy-MM-dd')
            return (
              itemLocalDateStr >= monthStart && itemLocalDateStr <= monthEnd
            )
          }
          if (operator === 'is_next_month') {
            const nextMonth = addMonths(today, 1)
            const monthStart = format(startOfMonth(nextMonth), 'yyyy-MM-dd')
            const monthEnd = format(endOfMonth(nextMonth), 'yyyy-MM-dd')
            return (
              itemLocalDateStr >= monthStart && itemLocalDateStr <= monthEnd
            )
          }
          if (operator === 'in_last_days') {
            const days = Number.parseInt(filter.value[0], 10)
            if (Number.isNaN(days) || days < 1) {
              return false
            }
            const rangeStart = format(subDays(today, days), 'yyyy-MM-dd')
            return (
              itemLocalDateStr >= rangeStart && itemLocalDateStr <= todayStr
            )
          }
          if (operator === 'in_next_days') {
            const days = Number.parseInt(filter.value[0], 10)
            if (Number.isNaN(days) || days < 1) {
              return false
            }
            const rangeEnd = format(addDays(today, days), 'yyyy-MM-dd')
            return itemLocalDateStr >= todayStr && itemLocalDateStr <= rangeEnd
          }

          if (operator === 'between') {
            try {
              const start = parseISO(filter.value[0])
              const end = parseISO(filter.value[1])
              if (!isValid(start) || !isValid(end)) {
                return false
              }
              const startLocalDateStr = format(start, 'yyyy-MM-dd')
              const endLocalDateStr = format(end, 'yyyy-MM-dd')
              return (
                itemLocalDateStr >= startLocalDateStr &&
                itemLocalDateStr <= endLocalDateStr
              )
            } catch {
              return false
            }
          }
          const filterDate = parseISO(filter.value[0])
          if (!isValid(filterDate)) {
            return false
          }

          if (operator === 'equals') {
            const filterLocalDateStr = format(filterDate, 'yyyy-MM-dd')
            return itemLocalDateStr === filterLocalDateStr
          }
          if (operator === 'not-equals') {
            const filterLocalDateStr = format(filterDate, 'yyyy-MM-dd')
            return itemLocalDateStr !== filterLocalDateStr
          }
          if (operator === 'before') {
            const filterLocalDateStr = format(filterDate, 'yyyy-MM-dd')
            return itemLocalDateStr < filterLocalDateStr
          }
          if (operator === 'after') {
            const filterLocalDateStr = format(filterDate, 'yyyy-MM-dd')
            return itemLocalDateStr > filterLocalDateStr
          }

          return true
        }

        if (filterConfig.type === 'boolean') {
          const itemValue = filterConfig.valueFn(item)
          const filterBool = filter.value[0] === 'true'
          return itemValue === filterBool
        }

        return true
      })
    )
  }, [data, filterOptions, mappedFilters])
}

export const FilterValue = {
  CurrentUser: 'current-user',
  None: 'none',
} as const
